import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useAccount } from 'wagmi';
import ChainPicker from './ChainPicker';
import BridgeArrow from './BridgeArrow';
import KStableAmountInput from './KStableAmountInput';
import BridgeSummary from './BridgeSummary';
import { BRAND } from '../theme';
import {
  parseUnits,
  computeBridgeFeeWei,
  computeBridgeNetWei,
  cmpWei,
  formatUnits,
} from '../lib/wei';
import { kStableInfo, chainName } from '../_shared/wallet-app/chains';
import {
  encodeApproveCalldata,
  encodeRequestSwapCalldata,
  newIdempotencyKeyHex,
} from '../lib/bridgeCalldata';
import { RequireCapability } from '../_shared/wallet-app/wallet-rbac/RequireCapability';
import { CAPABILITIES } from '../_shared/wallet-app/wallet-rbac/capabilities';

// BridgeForm
//
// The mid-page card with the chain selectors, amount input, recipient
// address, and Bridge button. State machine:
//   idle -> submitting -> intent_created  (then parent navigates to status)
//
// Two-tx orchestration (approve + initiateBurn) actually happens on the
// status page once we have the intent's calldata. From the form's point of
// view, success == "we have an intentId; route to /bridge/<id>".
//
// Props:
//   pairs              Array of supported pairs from GET /bridge/chains
//   pairsLoading       bool
//   onIntent           (intentId) => void   parent navigates on success
//   onPairChanged      (src, dst) => void   for the parent to fetch balance, etc.
//   sourceBalance      BigInt | null        balance of selected symbol on src
//   sourceBalanceLine  Optional text shown under the Source picker
//   destBalance        BigInt | null        projected dest balance line

export default function BridgeForm({
  pairs = [],
  pairsLoading = false,
  onIntent,
  onPairChanged,
  sourceBalance = null,
  destBalance = null,
}) {
  const { address, isConnected } = useAccount();

  // ── Derived chain + symbol catalogues ──
  const allSourceIds = useMemo(() => {
    return [...new Set(pairs.map((p) => p.src.id))];
  }, [pairs]);

  const [srcChainId, setSrcChainId] = useState('');
  const [dstChainId, setDstChainId] = useState('');
  const [symbol, setSymbol] = useState('kUSD');
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Init: first available pair.
  useEffect(() => {
    if (!srcChainId && pairs.length > 0) {
      const first = pairs[0];
      setSrcChainId(first.src.id);
      setDstChainId(first.dst.id);
      if (Array.isArray(first.supportedKStables) && !first.supportedKStables.includes(symbol)) {
        setSymbol(first.supportedKStables[0] || 'kUSD');
      }
    }
  }, [pairs, srcChainId, symbol]);

  // Default recipient = connected wallet.
  useEffect(() => {
    if (address && !recipient) setRecipient(address);
  }, [address, recipient]);

  // Notify parent on pair change (parent fetches balance).
  useEffect(() => {
    if (srcChainId && dstChainId && onPairChanged) {
      onPairChanged(Number(srcChainId), Number(dstChainId), symbol);
    }
  }, [srcChainId, dstChainId, symbol, onPairChanged]);

  // ── Picker options derived from pairs ──
  const sourceOptions = useMemo(() => {
    return allSourceIds.map((id) => ({ chainId: id, available: true }));
  }, [allSourceIds]);

  const destOptions = useMemo(() => {
    const ids = pairs.filter((p) => p.src.id === srcChainId).map((p) => p.dst.id);
    return [...new Set(ids)].map((id) => ({ chainId: id, available: true }));
  }, [pairs, srcChainId]);

  // Active pair (matched src+dst).
  const activePair = useMemo(() => {
    return pairs.find(
      (p) => p.src.id === Number(srcChainId) && p.dst.id === Number(dstChainId),
    );
  }, [pairs, srcChainId, dstChainId]);

  const supportedSymbols = activePair?.supportedKStables ?? null;
  // kStable-specific route info (addresses + fee schedule) — falls back to
  // the pair-level defaults (phase 1 is single-symbol so these agree).
  const kInfo = activePair?.kStables?.[symbol] ?? null;
  const feeBps = kInfo?.protocolFeeBps ?? activePair?.feeBps ?? 5;
  const gasFeeRawWei = kInfo?.gasFeeRawWei ?? activePair?.gasFeeRawWei ?? '0';
  const etaSeconds = activePair?.estimatedRelaySeconds ?? 120;

  // Amount math (BigInt only). MUST mirror BridgeSenderUpgradeable.requestSwap's
  // fee formula exactly (bps + flat gas fee) — see lib/wei.js computeBridgeNetWei.
  // Getting this wrong under-estimates the fee, which would make minOutAmount
  // too high and every requestSwap revert with InsufficientNetAfterFees.
  const { amountWei, feeWei, netWei, parseError } = useMemo(() => {
    if (!amount) return { amountWei: 0n, feeWei: 0n, netWei: 0n, parseError: null };
    const decimals = kStableInfo(symbol)?.decimals ?? 6;
    try {
      const amt = parseUnits(amount, decimals);
      const fee = computeBridgeFeeWei(amt, BigInt(feeBps), BigInt(gasFeeRawWei));
      const net = computeBridgeNetWei(amt, BigInt(feeBps), BigInt(gasFeeRawWei));
      if (amt > 0n && net === 0n) {
        return { amountWei: amt, feeWei: fee, netWei: net, parseError: 'Amount too small after fees.' };
      }
      return { amountWei: amt, feeWei: fee, netWei: net, parseError: null };
    } catch (e) {
      return { amountWei: 0n, feeWei: 0n, netWei: 0n, parseError: e.message };
    }
  }, [amount, symbol, feeBps, gasFeeRawWei]);

  const overBalance = useMemo(() => {
    if (!sourceBalance || amountWei === 0n) return false;
    return cmpWei(amountWei, sourceBalance) > 0;
  }, [amountWei, sourceBalance]);

  const validRecipient = /^0x[a-fA-F0-9]{40}$/.test(recipient || '');

  const canSubmit =
    isConnected &&
    !!srcChainId &&
    !!dstChainId &&
    srcChainId !== dstChainId &&
    amountWei > 0n &&
    !overBalance &&
    !parseError &&
    validRecipient &&
    !submitting;

  // ── Submit handler ──
  // Fully client-side: no server call. Builds the two calldata blobs the
  // customer's own wallet will sign (approve + requestSwap) from the
  // addresses published by GET /bridge/routes, and hands a locally-generated
  // "plan" to the parent, which stashes it (sessionStorage) and navigates to
  // the status page — the SAME two-page flow as before, just without a
  // server-tracked intent in the middle.
  const onSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      if (!kInfo) {
        setError('This kStable is not available on the selected route.');
        setSubmitting(false);
        return;
      }
      const idempotencyKeyHex = newIdempotencyKeyHex();
      const approvalCalldata = encodeApproveCalldata(
        kInfo.srcBridgeSenderAddress,
        amountWei,
      );
      const bridgeCalldata = encodeRequestSwapCalldata({
        kStableAddress: kInfo.srcKStableAddress,
        amountWei,
        destChainId: Number(dstChainId),
        recipient,
        minOutWei: netWei,
        idempotencyKeyHex,
      });
      const localId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const plan = {
        localId,
        srcChainId: Number(srcChainId),
        dstChainId: Number(dstChainId),
        kStableSymbol: symbol,
        amountWei: amountWei.toString(),
        feeWei: feeWei.toString(),
        netWei: netWei.toString(),
        recipient,
        idempotencyKeyHex,
        srcKStableAddress: kInfo.srcKStableAddress,
        dstKStableAddress: kInfo.dstKStableAddress,
        srcBridgeSenderAddress: kInfo.srcBridgeSenderAddress,
        dstBridgeReceiverAddress: kInfo.dstBridgeReceiverAddress,
        decimals,
        approvalCalldata,
        bridgeCalldata,
        createdAt: Date.now(),
      };
      if (typeof onIntent === 'function') {
        onIntent(localId, plan);
      }
    } catch (e) {
      setError(e?.message || 'Could not prepare the bridge transaction.');
      setSubmitting(false);
    }
  };

  const swapDirection = () => {
    setSrcChainId(dstChainId);
    setDstChainId(srcChainId);
  };

  const decimals = kStableInfo(symbol)?.decimals ?? 6;

  return (
    <Card
      sx={{
        maxWidth: 720,
        mx: 'auto',
        position: 'relative',
        overflow: 'visible',
      }}
      data-testid="bridge-form"
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        {pairsLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        )}
        {!pairsLoading && pairs.length === 0 && (
          <Alert severity="info">
            No bridge routes available yet. Please check back shortly.
          </Alert>
        )}
        {!pairsLoading && pairs.length > 0 && (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0, sm: 1.5 }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mb: 2.5 }}
            >
              <ChainPicker
                label="From"
                value={srcChainId}
                onChange={setSrcChainId}
                options={sourceOptions}
                partnerId={Number(dstChainId)}
                balanceLine={
                  sourceBalance != null
                    ? `Balance: ${formatUnits(sourceBalance, decimals)} ${symbol}`
                    : null
                }
                testIdSuffix="source"
              />
              <BridgeArrow onSwap={swapDirection} />
              <ChainPicker
                label="To"
                value={dstChainId}
                onChange={setDstChainId}
                options={destOptions}
                partnerId={Number(srcChainId)}
                balanceLine={
                  destBalance != null
                    ? `Projected: ${formatUnits(destBalance + netWei, decimals)} ${symbol}`
                    : null
                }
                testIdSuffix="dest"
              />
            </Stack>

            <KStableAmountInput
              symbol={symbol}
              onSymbolChange={setSymbol}
              amount={amount}
              onAmountChange={setAmount}
              decimals={decimals}
              balanceWei={sourceBalance}
              supportedSymbols={supportedSymbols}
              error={parseError}
            />

            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Recipient on destination"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="0x…"
                helperText={
                  validRecipient || !recipient
                    ? 'Defaults to your connected wallet. Edit to send to another wallet on the destination chain.'
                    : 'Not a valid Ethereum address.'
                }
                error={!!recipient && !validRecipient}
                inputProps={{ 'data-testid': 'recipient-input', spellCheck: false }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    '& fieldset': { borderColor: BRAND.border },
                    '&:hover fieldset': { borderColor: BRAND.violet },
                  },
                }}
              />
            </Box>

            <BridgeSummary
              amountWei={amountWei}
              feeBps={feeBps}
              feeWei={feeWei}
              netWei={netWei}
              estimatedSeconds={etaSeconds}
              symbol={symbol}
              dstChainId={Number(dstChainId)}
              decimals={decimals}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {!isConnected && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Connect your wallet to bridge.
              </Alert>
            )}

            <RequireCapability cap={CAPABILITIES.BRIDGE_SWAP}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={onSubmit}
                disabled={!canSubmit}
                endIcon={
                  submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
                }
                sx={{ mt: 2.5, fontSize: '1rem' }}
                data-testid="bridge-submit"
              >
                {submitting
                  ? 'Preparing bridge…'
                  : amountWei > 0n && validRecipient && srcChainId !== dstChainId
                    ? `Bridge ${formatUnits(amountWei, decimals)} ${symbol} → ${chainName(Number(dstChainId))}`
                    : 'Bridge'}
              </Button>
            </RequireCapability>

            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1.5, color: BRAND.textSecondary, textAlign: 'center' }}
            >
              Two signatures: an ERC20 approve to authorise the bridge, then
              the burn that emits the cross-chain message. Operator mints on
              the destination chain.
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}
